/**
 * StatCard - Quick stats display card
 *
 * Displays a single metric with icon, value, and label.
 * Used on dashboards for at-a-glance metrics.
 */

import { type LucideIcon } from 'lucide-react';

export interface StatCardProps {
  value: string | number;
  label: string;
  color: string;
  icon: LucideIcon;
  onClick?: () => void;
  compact?: boolean;
}

export function StatCard({
  value,
  label,
  color,
  icon: Icon,
  onClick,
  compact = false,
}: StatCardProps) {
  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      className={`card ${onClick ? 'card-interactive' : ''}`}
      onClick={onClick}
      style={{
        padding: compact ? '16px' : '20px',
        cursor: onClick ? 'pointer' : 'default',
        width: '100%',
        border: 'none',
        textAlign: 'left',
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className={`icon-container ${compact ? 'icon-container-xs' : 'icon-container-sm'}`}
          style={{ background: `${color}15` }}
        >
          <Icon size={compact ? 16 : 18} style={{ color }} />
        </div>
        <div>
          <p
            style={{
              fontSize: compact ? '20px' : '24px',
              fontWeight: '700',
              color: 'var(--gray-900)',
              lineHeight: 1.2,
            }}
          >
            {value}
          </p>
          <p
            style={{
              fontSize: compact ? '12px' : '13px',
              color: 'var(--gray-500)',
              marginTop: '2px',
            }}
          >
            {label}
          </p>
        </div>
      </div>
    </Component>
  );
}

export default StatCard;
